import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Badge, 
  Button, 
  Modal, 
  Form, 
  Alert,
  Spinner,
  Table
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { aiService, Camera } from '../../../services/aiService';

const CameraManagement: React.FC = () => {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCameras();
  }, []);

  const loadCameras = async () => {
    try {
      setLoading(true);
      const data = await aiService.getCameras();
      setCameras(data);
      setError(null);
    } catch (err) {
      setError('無法載入攝像頭列表');
      console.error('載入攝像頭列表失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCamera = (camera: Camera) => {
    setSelectedCamera({ ...camera });
    setShowModal(true);
  };

  const handleSaveCamera = async () => {
    if (!selectedCamera) return;

    try {
      setSaving(true);
      await aiService.updateCamera(selectedCamera._id, selectedCamera);
      toast.success('攝像頭配置已更新');
      setShowModal(false);
      await loadCameras();
    } catch (err) {
      toast.error('更新攝像頭配置失敗');
      console.error('更新攝像頭配置失敗:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (!selectedCamera) return;

    setSelectedCamera(prev => {
      if (!prev) return prev;
      
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        const parentValue = prev[parent as keyof Camera];
        return {
          ...prev,
          [parent]: {
            ...(typeof parentValue === 'object' && parentValue !== null ? parentValue : {}),
            [child]: value
          }
        };
      }
      
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'danger';
      case 'maintenance':
        return 'warning';
      case 'error':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    const statusNames: Record<string, string> = {
      online: '在線',
      offline: '離線',
      maintenance: '維護中',
      error: '錯誤'
    };
    return statusNames[status] || status;
  };

  const getDetectionTypeText = (type: string) => {
    const typeNames: Record<string, string> = {
      abnormal_behavior: '異常行為',
      fire_smoke: '火災煙霧',
      fall_detection: '跌倒檢測',
      vehicle_collision: '車輛碰撞'
    };
    return typeNames[type] || type;
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">攝像頭管理</h5>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={loadCameras}
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-1"></i>
              刷新
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        {cameras.map((camera) => (
          <Col lg={6} xl={4} key={camera._id} className="mb-4">
            <Card className="h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-1">{camera.name}</h6>
                  <Badge bg={getStatusColor(camera.status)}>
                    {getStatusText(camera.status)}
                  </Badge>
                </div>
                <div>
                  <Badge bg={camera.enabled ? 'success' : 'secondary'}>
                    {camera.enabled ? '啟用' : '禁用'}
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <small className="text-muted">位置</small>
                  <div>{camera.location.zone} - {camera.location.description}</div>
                </div>

                <div className="mb-3">
                  <small className="text-muted">規格</small>
                  <div className="small">
                    <div>解析度: {camera.specs.resolution}</div>
                    <div>幀率: {camera.specs.fps} FPS</div>
                    <div>視野角: {camera.specs.fieldOfView}°</div>
                    <div>
                      夜視: {camera.specs.nightVision ? '支持' : '不支持'}
                      <span className="mx-2">•</span>
                      音頻: {camera.specs.audioCapture ? '支持' : '不支持'}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted">AI檢測功能</small>
                  <div>
                    {camera.aiConfig.enabledDetections.map((type) => (
                      <Badge key={type} bg="info" className="me-1 mb-1">
                        {getDetectionTypeText(type)}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted">網絡配置</small>
                  <div className="small">
                    <div>IP: {camera.network.ipAddress}</div>
                    <div>串流URL: {camera.network.streamUrl}</div>
                  </div>
                </div>
              </Card.Body>
              <Card.Footer>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleEditCamera(camera)}
                >
                  <i className="fas fa-edit me-1"></i>
                  編輯配置
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      {cameras.length === 0 && (
        <div className="text-center py-4 text-muted">
          <i className="fas fa-video display-4 mb-3"></i>
          <p className="mb-0">沒有找到攝像頭</p>
        </div>
      )}

      {/* 編輯攝像頭模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>編輯攝像頭配置</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCamera && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>攝像頭名稱</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedCamera.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>狀態</Form.Label>
                    <Form.Select
                      value={selectedCamera.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <option value="online">在線</option>
                      <option value="offline">離線</option>
                      <option value="maintenance">維護中</option>
                      <option value="error">錯誤</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>區域</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedCamera.location.zone}
                      onChange={(e) => handleInputChange('location.zone', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>位置描述</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedCamera.location.description}
                      onChange={(e) => handleInputChange('location.description', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>IP地址</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedCamera.network.ipAddress}
                      onChange={(e) => handleInputChange('network.ipAddress', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>串流URL</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedCamera.network.streamUrl}
                      onChange={(e) => handleInputChange('network.streamUrl', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="啟用攝像頭"
                  checked={selectedCamera.enabled}
                  onChange={(e) => handleInputChange('enabled', e.target.checked)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>AI檢測功能</Form.Label>
                <div>
                  {['abnormal_behavior', 'fire_smoke', 'fall_detection', 'vehicle_collision'].map((type) => (
                    <Form.Check
                      key={type}
                      type="checkbox"
                      label={getDetectionTypeText(type)}
                      checked={selectedCamera.aiConfig.enabledDetections.includes(type)}
                      onChange={(e) => {
                        const newDetections = e.target.checked
                          ? [...selectedCamera.aiConfig.enabledDetections, type]
                          : selectedCamera.aiConfig.enabledDetections.filter(t => t !== type);
                        handleInputChange('aiConfig.enabledDetections', newDetections);
                      }}
                    />
                  ))}
                </div>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveCamera}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                保存中...
              </>
            ) : (
              '保存配置'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CameraManagement;